import type { ReactElement } from "react";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import type {
  Task,
  ValidatorProgress,
  ValidatorResults,
} from "@/core/state-shape";

export type ValidationSummaryStatus =
  | "running"
  | "unknown"
  | "failed"
  | "passed";

export interface ValidationSummary {
  text: string;
  status: ValidationSummaryStatus;
  icon: ReactElement;
}

export const getValidationSummary = (
  validation: ValidatorResults | undefined,
  activeRun: ValidatorProgress | undefined,
  taskStatus: Task["status"] | undefined
): ValidationSummary => {
  if (activeRun) {
    return {
      text: `Validating... (${activeRun.phase?.replace(/_/g, " ") || "initializing"})`,
      status: "running",
      icon: (
        <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
      ),
    };
  }
  if (taskStatus === "validating") {
    return {
      text: "Validating...",
      status: "running",
      icon: (
        <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
      ),
    };
  }

  if (!validation) {
    return {
      text: "Not run yet",
      status: "unknown",
      icon: <AlertCircle className="h-4 w-4 text-muted-foreground" />,
    };
  }

  const lintFailed = validation.lint?.failed || 0;
  const typecheckFailed = validation.typecheck?.failed || 0;
  const testsFailed = validation.tests?.failed || 0;
  const totalFailed = lintFailed + typecheckFailed + testsFailed;

  if (validation.status !== "complete") {
    return {
      text: `Run is ${validation.status}`,
      status: "running",
      icon: <AlertCircle className="h-4 w-4 text-amber-500" />,
    };
  }
  if (totalFailed > 0) {
    return {
      text: `${totalFailed} issue(s) found`,
      status: "failed",
      icon: <AlertCircle className="h-4 w-4 text-destructive" />,
    };
  }

  return {
    text: "All checks passed",
    status: "passed",
    icon: <CheckCircle className="h-4 w-4 text-green-500" />,
  };
};